import { useState, useEffect, useCallback, useMemo } from 'react';
import { DataGridPro, GridInputRowSelectionModel, GridColDef } from '@mui/x-data-grid-pro';
import { SubMenu } from './SubMenu';
import { useTranslation } from 'react-i18next';
import { TimekeeperService } from 'services/TimekeeperService';
import { Grant } from 'repository/timekeeper/@types/Grant';

const GrantsTable = () => {
    const { t } = useTranslation();
    const [rows, setRows] = useState<Grant[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedAccrualRule, setSelectedAccrualRule] = useState<GridInputRowSelectionModel | undefined>([]);
    const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number } | null>(null);
    const [selected, setSelected] = useState<Grant[]>([]);

    const timekeeperService = useMemo(() => new TimekeeperService(), []);

    const columns: GridColDef[] = [
        {
            field: 'name',
            headerName: t('accruals.grants.table.columns.name'),
            flex: 1,
        },
        {
            field: 'grant_type',
            headerName: t('accruals.grants.table.columns.grant_type'),
            flex: 1,
            headerAlign: 'center',
            align: 'center',
            valueGetter: (value, row) => row.grant_type ?? '-',
        },
        {
            field: 'date_pattern',
            headerName: t('accruals.grants.table.columns.date_pattern'),
            flex: 1,
            headerAlign: 'center',
            align: 'center',
            valueGetter: (value, row) => {
                if (row.grant_type === 'FIJO' && row.date_pattern) {
                    return row.date_pattern.name || '-';
                }
                return '-';
            },
        },
        {
            field: 'expiration',
            headerName: t('accruals.grants.table.columns.expiration'),
            flex: 1,
            headerAlign: 'center',
            align: 'center',
            valueGetter: (value, row) => {
                if (row.expiration === true) {
                    const period = `${row.expiration_number} ${t(`accruals.grants.table.values.${row.expiration_unit_date}`).toLowerCase()}`;
                    return `${t('accruals.grants.table.values.expires')}: ${period}`;
                }
                return t('accruals.grants.table.values.no_expiration');
            },
        },
    ];

    const reloading = useCallback(() => {
        setLoading(true);
        timekeeperService.grants
            .getAll()
            .then((data: Grant[]) => {
                const orderedData = data.sort((a, b) => a.name.localeCompare(b.name));
                setRows(orderedData);
            })
            .catch((error) => {
                console.error('Error al obtener las concesiones:', error);
                setRows([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [timekeeperService.grants]);

    useEffect(() => {
        reloading();
    }, [reloading]);

    const cleanSelected = () => {
        setSelected([]);
        setSelectedAccrualRule([]);
    };

    return (
        <div
            style={{ width: '100%', height: '100%' }}
            onContextMenu={(event) => {
                event.preventDefault();
                setContextMenu(contextMenu === null ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 } : null);
            }}
        >
            <DataGridPro
                sx={{ height: 'calc(100vh - 140px)' }}
                columns={columns}
                rows={rows}
                loading={loading}
                rowSelectionModel={selectedAccrualRule}
                pagination
                onRowSelectionModelChange={(newRowSelectionModel) => {
                    let row = rows.filter((row) => newRowSelectionModel.includes(row.id));
                    setSelectedAccrualRule(newRowSelectionModel);
                    setSelected(row);
                }}
                checkboxSelection
                localeText={{
                    headerFilterOperatorContains: t('general.table.headerFilters.headerFilterOperatorContains'),
                    headerFilterOperatorEquals: t('general.table.headerFilters.headerFilterOperatorEquals'),
                    headerFilterOperatorStartsWith: t('general.table.headerFilters.headerFilterOperatorStartsWith'),
                    headerFilterOperatorEndsWith: t('general.table.headerFilters.headerFilterOperatorEndsWith'),
                    headerFilterOperatorIsEmpty: t('general.table.headerFilters.headerFilterOperatorIsEmpty'),
                    headerFilterOperatorIsNotEmpty: t('general.table.headerFilters.headerFilterOperatorIsNotEmpty'),
                    footerRowSelected: (count) => (count > 1 ? `${count.toLocaleString()} filas seleccionadas` : `${count.toLocaleString()} fila seleccionada`),

                    footerTotalRows: t('general.table.footerFilters.footerTotalRows'),
                    MuiTablePagination: {
                        labelDisplayedRows: ({ from, to, count }) => `${from} - ${to} of ${count === -1 ? `more than ${to}` : count}`,
                        labelRowsPerPage: t('general.table.footerFilters.labelRowsPerPage'),
                    },
                }}
                showCellVerticalBorder
                showColumnVerticalBorder
                disableColumnMenu
                headerFilters
                slotProps={{
                    toolbar: {
                        showQuickFilter: true,
                    },
                }}
            />
            <SubMenu selected={selected} cleanSelected={cleanSelected} rows={rows} contextMenu={contextMenu} setContextMenu={setContextMenu} reloading={reloading} />
        </div>
    );
};

export { GrantsTable };
